import React, { useCallback, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter } from 'react-router-dom';

import { TiModalColorScheme } from '@components/TIModal';
import { AppContextProvider, Env } from '@contexts/AppContext';
import { BlueconicProvider } from '@contexts/BlueconicContext';
import useBlueconicEvent from '@hooks/useBlueconicEvent';
import { EventProperties } from '@tracking';
import { User } from '@utils/backend-api/currentUser';
import { modalSubject } from '@utils/blueconic/constants';
import { AnyBlueconicEvent } from '@utils/blueconic/eventsInterfaces';
import { getBlueconicModalProps } from '@utils/blueconic/utils';

import ModalLayout from './ModalLayout';
import { LeadCaptureProps } from './modals/LeadCaptureModal';

const queryClient = new QueryClient();

export interface MarketingCopyProps extends LeadCaptureProps {
  analytics?: EventProperties;
  customUrl?: string;
  isGdprRequired?: boolean;
  onClose?: () => void;
}

export interface MarketingModalProps extends MarketingCopyProps {
  articleSlugs?: string[];
  articleTitleSection?: string;
  env?: Env;
  externalSource?: string;
  externalSourceId?: string;
  guid: string;
  highlightedCta: string;
  id?: string;
  imgPathLarge?: string;
  imgPathMedium?: string;
  imgPathSmall?: string;
  initialCurrentUser?: User;
  isActiveUser?: boolean;
  leadButtonText?: string;
  modalContainerClass?: string;
  modalParentClass?: string;
  modalTheme?: TiModalColorScheme;
  modalId?: string;
  name?: string;
}

const MarketingModal = (props: MarketingModalProps) => {
  const [initialProps, setInitialProps] = useState<MarketingModalProps>(props);
  const { initialCurrentUser, isGdprRequired, env } = initialProps;

  useBlueconicEvent();

  const handleBlueconicEvent = useCallback((e: AnyBlueconicEvent) => {
    const incomingProps = getBlueconicModalProps(e);
    if (!incomingProps) return;

    setInitialProps((prevState) => ({
      ...prevState,
      ...incomingProps,
    }));
  }, []);

  useEffect(() => {
    modalSubject.subscribe((data) => {
      handleBlueconicEvent(data);
    });
  }, [handleBlueconicEvent]);

  if (!initialProps.modalType) {
    return null;
  }

  return (
    <AppContextProvider
      initialCurrentUser={initialCurrentUser}
      isGdprRequired={isGdprRequired}
      env={env}
    >
      <BlueconicProvider variantId={initialProps.externalSourceId}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ModalLayout {...initialProps} />
          </BrowserRouter>
        </QueryClientProvider>
      </BlueconicProvider>
    </AppContextProvider>
  );
};

export default MarketingModal;
